import React, { useState, useEffect } from "react";
import {
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Button,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Typography,
  Divider,
  InputLabel,
  Checkbox,
  OutlinedInput,
  ListItemText
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { useAlert } from "../alerts/AlertContext";
import AxiosInstance from "../../services/AxiosInstance";
import { LICENSE_URL } from "../../Constants/Constants";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { ThemeProvider } from "../v2/home/theme/ThemeProvider";
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import moment from "moment";
import {UploadFile, Delete} from '@mui/icons-material';

function LicenseForm({ open, onClose, editData = null, onSuccess, isEditMode = false }) {
  
  const [loading, setLoading] = useState(false);
  
  const [productId, setProductId] = useState("");
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [files, setFile] = useState();
  const showAlert = useAlert();
  const [license, setLicense] = useState(null);
  // Initialize form data when dialog opens or editData changes
  useEffect(() => {
    if (open) {
      
      if (editData) {
       
        setProductId(editData.productId);  
        setEmail(editData.email);        
        setStartDate(editData.startDate);
        setEndDate(editData.endDate);
      } else {
        // setFormData({
        //   name: "",
        //   seq: "",
        //   active: true,
        // });
      }
      //setErrors({});
    }
  }, [open, editData]);


  const handleProductIdChange = (event) => {
    setProductId(event.target.value);
  };

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };

  const handleTokenChange = (event) => {
    setToken(event.target.value);
  };

 const handleFileChange = (event) => {
    const file = event.target.files[0];
    
    if (!file) return;
    
    const reader = new FileReader();

    reader.onload = (event) => {
      const text = event.target.result;
      const parsed = parseLicenseFile(text);
      setLicense(parsed);
    }

     reader.onerror = function (err) {
      alert("File read error:"+ err);
    };

    // VERY IMPORTANT: use correct method
    reader.readAsText(file);
 }
  
 const parseLicenseFile = (text) => {
    const lines = text.split("\n").map((l) => l.trim());

    let licenseObj = {
      LicenseVersion: null,
      ProductID: null,
      UserEmail: null,
      ValidFrom: null,
      ValidUntil: null,
      PublicKey: null,
      Token: null,
    };

    let insidePublicKey = false;
    let publicKeyLines = [];

    lines.forEach((line) => {
      if (line.startsWith("LicenseVersion:")) {
        licenseObj.LicenseVersion = line.split(":")[1].trim();
      } else if (line.startsWith("ProductID:")) {
        licenseObj.ProductID = line.split(":")[1].trim();
      } else if (line.startsWith("UserEmail:")) {
        licenseObj.UserEmail = line.split(":")[1].trim();
      } else if (line.startsWith("ValidFrom:")) {
        licenseObj.ValidFrom = line.split(":")[1].trim();
      } else if (line.startsWith("ValidUntil:")) {
        licenseObj.ValidUntil = line.split(":")[1].trim();
      }

      // Detect BEGIN PUBLIC KEY
      else if (line === "-----BEGIN PUBLIC KEY-----") {
        insidePublicKey = true;
        publicKeyLines.push(line);
      }

      // Capture lines inside public key
      else if (insidePublicKey) {
        publicKeyLines.push(line);

        if (line === "-----END PUBLIC KEY-----") {
          insidePublicKey = false;
          licenseObj.PublicKey = publicKeyLines.join("\n");
        }
      }

      // Token line
      else if (line.startsWith("Token:")) {
        licenseObj.Token = line.replace("Token:", "").trim();
      }
    });

    return licenseObj;
  };
    
  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {

      const payload = {
        publicKey: license.PublicKey,
        token: license.Token,        
        ...(isEditMode && { id: editData.id }),
      };

      await AxiosInstance.post(LICENSE_URL, payload);

      showAlert(
        `License ${isEditMode ? "updated" : "created"} successfully!`,
        "success"
      );

      onSuccess?.();
      onClose();
    } catch (error) {
      showAlert("Error saving License:", "error");
      showAlert(
        `License ${isEditMode ? "update" : "creation"} failed!`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: 450,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >      
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isEditMode ? (
            <EditIcon color="primary" />
          ) : (
            <AddIcon color="primary" />
          )}
          <Typography variant="h6" component="span">
            {isEditMode ? "Edit License" : "Add New License"}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={loading}
          size="small"
          sx={{ color: "grey.500" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <ThemeProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Box
              sx={{
                display: "flex",
                gap: 2,          // space between dropdowns
                width: "100%",   // full parent width
              }}
            >
              {/* <TextField
                autoFocus
                required
                margin="dense"
                id="outlined-multiline-static"
                name="token"
                label="Token"
                type="text"
                variant="outlined"
                fullWidth
                //value={productId}
                onChange={handleTokenChange}                
              /> */}

               <Box sx={{display: 'flex', justifyContent: 'flex-start'}}>
                  <Button
                      variant="outlined"
                      size="small"
                      component="label"
                      startIcon={<UploadFile/>}
                      sx={{
                          fontSize: '0.75rem',
                          padding: '2px 6px',
                          textTransform: 'none',
                          minWidth: 'auto',
                      }}
                  >
                      Upload Files
                      <input type="file" hidden multiple onChange={handleFileChange}/>
                  </Button>
              </Box>

              <Button
                onClick={handleSubmit}
                disabled={loading}
                variant="contained"
                startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
                sx={{
                  borderRadius: 2,
                  minWidth: 120,
                  maxHeight: 50

                }}
              >
                {loading ? "Saving..." : isEditMode ? "Update" : "Add"}
              </Button>
              
            </Box>   
            <Box
              sx={{
                display: "flex",
                gap: 2,          // space between dropdowns
                width: "100%",   // full parent width
              }}
            >
             <TextField
                autoFocus
                required
                margin="dense"
                id="outlined-multiline-static"
                name="productId"
                label="Product Id"
                type="text"
                variant="outlined"
                fullWidth
                value={productId}
                onChange={handleProductIdChange}
                disabled
              />
              <TextField
                autoFocus
                required
                margin="dense"
                id="outlined-multiline-static"
                name="email"
                label="Email"
                type="text"
                variant="outlined"
                fullWidth
                value={email}
                onChange={handleEmailChange}
                disabled
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 2,          // space between dropdowns
                width: "100%",   // full parent width
              }}
            >
            <TextField
                autoFocus
                required
                margin="dense"
                id="outlined-multiline-static"
                name="startDate"
                label="Start Date"
                type="text"
                variant="outlined"
                fullWidth
                value={startDate}                
                disabled
              />
              <TextField
                autoFocus
                required
                margin="dense"
                id="outlined-multiline-static"
                name="endDate"
                label="End Date"
                type="text"
                variant="outlined"
                fullWidth
                value={endDate}                
                disabled
              />
            </Box>

          <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
            {isEditMode
              ? "You are editing an existing notification."
              : "You are adding a new notification."}
          </Alert>          
        </Box>
        </LocalizationProvider>
        </ThemeProvider>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          sx={{ borderRadius: 2 }}
        >
          Close
        </Button>
        
      </DialogActions>
    </Dialog>
  );
}

export default LicenseForm;
